'use client'

import { SiFacebook, SiX } from '@icons-pack/react-simple-icons'
import {
  Check,
  ChevronRight,
  Link as LinkIcon,
  Mail,
  Share2,
} from 'lucide-react'
import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/src/components/ui/tooltip'
import { cn } from '~/src/lib/utils'

interface BreadcrumbItem {
  label: string
  /** When null the crumb renders as plain text (current page). */
  href: string | null
}

interface SeoReportHeaderProps {
  breadcrumbs: BreadcrumbItem[]
  h1: string
  freshnessStatement: string
  shareUrl: string
  productsAnalyzed?: number | null
  sourcesReviewed?: number | null
  onSourcesClick?: () => void
}

interface HeaderStat {
  key: string
  label: string
  onClick?: () => void
}

function ShareButton({
  label,
  onClick,
  children,
  tooltipLabel,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
  tooltipLabel?: string
}) {
  const button = (
    <button
      type='button'
      aria-label={label}
      onClick={onClick}
      className='flex size-10 items-center justify-center rounded-full border border-border bg-white text-ds-fg-secondary transition-colors hover:bg-gray-50'
    >
      {children}
    </button>
  )
  if (!tooltipLabel) return button
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side='top' sideOffset={6}>
        {tooltipLabel}
      </TooltipContent>
    </Tooltip>
  )
}

export function SeoReportHeader({
  breadcrumbs,
  h1,
  freshnessStatement,
  shareUrl,
  productsAnalyzed,
  sourcesReviewed,
  onSourcesClick,
}: SeoReportHeaderProps) {
  const [copied, setCopied] = useState(false)
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
    }
  }, [])

  const handleCopyLink = useCallback(() => {
    void navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
    copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000)
  }, [shareUrl])

  const handleShareX = useCallback(() => {
    const url = `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(h1)}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [shareUrl, h1])

  const handleShareFacebook = useCallback(() => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [shareUrl])

  const handleShareEmail = useCallback(() => {
    const url = `mailto:?subject=${encodeURIComponent(h1)}&body=${encodeURIComponent(shareUrl)}`
    window.location.href = url
  }, [shareUrl, h1])

  const handleNativeShare = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      void navigator.share({ title: h1, url: shareUrl })
    }
  }, [shareUrl, h1])

  const dateText = freshnessStatement.includes(', as of ')
    ? freshnessStatement.split(', as of ')[1]?.replace(/\.$/, '')
    : freshnessStatement
  const stats: HeaderStat[] = []
  if (productsAnalyzed && productsAnalyzed > 0) {
    stats.push({
      key: 'products',
      label: `${productsAnalyzed} products`,
    })
  }
  if (sourcesReviewed && sourcesReviewed > 0) {
    stats.push({
      key: 'sources',
      label: `${sourcesReviewed} sources`,
      onClick: onSourcesClick,
    })
  }

  return (
    <header className='flex flex-col gap-3'>
      {/* Breadcrumbs -- single line; the current-page crumb truncates rather
          than wrapping so short names don't line-break awkwardly on mobile. */}
      <nav aria-label='Breadcrumb'>
        <ol className='flex min-w-0 items-center gap-1.5'>
          {breadcrumbs.map((crumb, index) => (
            <li
              key={`${crumb.href ?? 'current'}-${index}`}
              className={cn(
                'flex items-center gap-1.5',
                // Linked crumbs (category/subcategory) keep their width; the
                // final current-page crumb absorbs the overflow and truncates.
                crumb.href ? 'shrink-0' : 'min-w-0'
              )}
            >
              {index > 0 && (
                <ChevronRight
                  className='size-4 shrink-0 text-muted-foreground'
                  aria-hidden='true'
                />
              )}
              {crumb.href ? (
                <Link
                  href={crumb.href}
                  className='whitespace-nowrap text-sm font-normal text-ds-text-quaternary hover:text-ds-text-secondary'
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  aria-current='page'
                  className='truncate text-sm font-normal text-ds-text-quaternary'
                >
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>

      {/* H1 + meta */}
      <div className='flex flex-col gap-4'>
        <h1 className='text-balance font-display text-[30px] font-bold leading-[36px] tracking-[-1.2px] text-foreground md:text-4xl md:leading-[1.1] lg:text-5xl xl:text-[60px] xl:leading-[72px]'>
          {h1}
        </h1>
        <div className='flex flex-col gap-1 text-sm text-foreground-secondary'>
          <p>
            Updated <time>{dateText}</time>
          </p>
          {stats.length > 0 && (
            <p className='flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground'>
              {stats.map((stat, index) => (
                <span key={stat.key} className='inline-flex items-center'>
                  {stat.onClick ? (
                    <button
                      type='button'
                      onClick={stat.onClick}
                      className='rounded-sm text-left font-medium text-info-700 underline underline-offset-4 transition-colors hover:text-info-700/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                    >
                      {stat.label}
                    </button>
                  ) : (
                    stat.label
                  )}
                  {index < stats.length - 1 && (
                    <span aria-hidden='true' className='ml-2'>
                      ·
                    </span>
                  )}
                </span>
              ))}
            </p>
          )}
        </div>
      </div>

      {/* Share Buttons */}
      <TooltipProvider delayDuration={150}>
        <div className='flex items-center gap-2.5 pt-2'>
          <ShareButton
            label={copied ? 'Link copied' : 'Copy link'}
            tooltipLabel={copied ? 'Copied' : 'Copy link'}
            onClick={handleCopyLink}
          >
            {copied ? (
              <Check
                className='size-4 text-status-success-text'
                strokeWidth={2}
              />
            ) : (
              <LinkIcon className='size-4' strokeWidth={2} />
            )}
          </ShareButton>
          {/* Native share -- mobile only */}
          <span className='md:hidden'>
            <ShareButton label='Share' onClick={handleNativeShare}>
              <Share2 className='size-4' strokeWidth={2} />
            </ShareButton>
          </span>
          {/* Social share buttons -- desktop only */}
          <span className='hidden md:flex md:items-center md:gap-2.5'>
            <ShareButton label='Share on X' onClick={handleShareX}>
              <SiX className='size-3' />
            </ShareButton>
            <ShareButton
              label='Share on Facebook'
              onClick={handleShareFacebook}
            >
              <SiFacebook className='size-4' />
            </ShareButton>
            <ShareButton label='Share via email' onClick={handleShareEmail}>
              <Mail className='size-4' strokeWidth={2} />
            </ShareButton>
          </span>
        </div>
      </TooltipProvider>
    </header>
  )
}
